<template>
    <div
        v-if="modelValue"
        class="fixed top-0 w-full h-full bg-black/50 z-[80] flex justify-center items-center"
    >
        <div class="border rounded-02 bg-white w-[1024px] max-h-[85vh] flex flex-col overflow-y-auto">
            <div class="px-7 py-6 flex justify-between items-center">
                <span class="font-inter-heading-14 text-[#171717]"> {{ title || 'Select Employee' }} </span>
                <button
                    type="button"
                    class="ml-20 flex items-center justify-center"
                    @click="onClickCancelButton"
                >
                    <CloseIcon :width="24" :height="24" color="#171717" weight="REGULAR" />
                </button>
            </div>
            <div class="w-full h-px border-b border-[#ECECEC]"></div>

            <div class="flex justify-between items-center flex-wrap p-5">
                <div class="w-full flex justify-between items-center flex-wrap">
                    <div class="flex flex-wrap gap-2">
                        <DesignDropdownSelect
                            v-if="showWorkScheduleOption"
                            v-model="searchJsonQuery.workScheduleSeq"
                            :options="workScheduleOptions"
                            size="MD"
                            :width="'w-60'"
                            placeholder="All Work Schedule"
                            :useDefaultMsg="true"
                            @change="searchEmployeeData" />
                        <DesignDropdownSelect
                            v-if="showDepartmentOption"
                            v-model="searchJsonQuery.departmentSeq"
                            :options="departmentOptions"
                            size="MD"
                            :width="'w-60'"
                            placeholder="All Departments"
                            :useDefaultMsg="true"
                            @change="searchEmployeeData" />
                        <DesignDropdownSelect
                            v-if="showJobTitleOption"
                            v-model="searchJsonQuery.jobTitleSeq"
                            :options="jobTitleOptions"
                            size="MD"
                            :width="'w-60'"
                            placeholder="All Tiers"
                            :useDefaultMsg="true"
                            @change="searchEmployeeData" />
                        <DesignDropdownSelect
                            v-if="showEmploymentTypeOption"
                            v-model="searchJsonQuery.employmentType"
                            :options="employmentTypeOptions"
                            size="MD"
                            :width="'w-60'"
                            placeholder="All Contract Type"
                            :useDefaultMsg="true"
                            @change="searchEmployeeData" />
                        <div class="flex items-center gap-1">
                            <DesignSearchInput
                                v-model="searchInputString"
                                :options="employeeOptions"
                                size="MD"
                                :width="'w-80'"
                                :placeholder="'Search'"
                                @searchEvent="searchEmployeeData"
                                :optionClickEvent="true"
                            />
                            <DesignButton
                                class="w-16"
                                style="height: 40px"
                                btnText="Search"
                                btnSize="MD"
                                btnType="QUATERNARY"
                                btnVariant="BORDER"
                                :btnDisabled="false"
                                @click-event="searchEmployeeData"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div class="px-6 pt-4 pb-6 flex items-stretch gap-3 border-y border-[#ECECEC] h-[520px]">
                <div class="flex-1">
                    <div class="flex flex-col gap-3 h-full">
                        <div class="flex justify-start gap-2 items-center">
                            <DesignCheckbox
                                v-model="allCheck.isChecked"
                                :trueValue="true"
                                :falseValue="false"
                                :label="''"
                                @update:modelValue="clickAllCheckbox"
                            />
                            <p class="font-inter text-[16px] leading-[16px] font-normal text-[#666]">Select All</p>
                        </div>
                        <div class="border border-[#DDD] rounded-02 h-full overflow-y-auto ds-scrollbar">
                            <div class="min-w-full">
                                <div v-for="(employee, index) in filteredEmployeeList" v-bind:key="index">
                                    <label
                                        :for="employee.employeeSeq"
                                        class="h-16 flex justify-start items-center py-2.5 w-full"
                                        :class="{ 'border-b': index !== filteredEmployeeList.length - 1 }">
                                        <div class="w-[3.125rem] flex justify-center">
                                            <DesignCheckbox
                                                v-model="employee.isChecked"
                                                :trueValue="true"
                                                :falseValue="false"
                                                :id="employee.employeeSeq"
                                            />
                                        </div>

                                        <div class="w-[3.125rem] text-sm font-normal text-center truncate">
                                            {{ searchJsonQuery.offset + index + 1 }}
                                        </div>

                                        <div class="w-[20rem] flex items-center gap-2 px-4">
                                            <StatusCircle :status-code="employee.employeeStatus" />
                                            <div class="flex-1 flex-col">
                                                <p class="font-inter-body-2 leading-[20px] text-[#171717] line-clamp-1 break-all">
                                                    {{ employee.employeeName }}
                                                </p>
                                                <p class="font-inter-caption-3 leading-[17px] text-[#666] line-clamp-1 break-all">
                                                    {{ employee.departmentName }}
                                                </p>
                                            </div>
                                        </div>
                                    </label>
                                </div>
                            </div>
                        </div>
                        <div class="w-full h-10 flex justify-center items-center">
                            <DesignPagination
                                v-model="currentPage"
                                :totalContent="employeeCount"
                                :contentsPerPage="searchJsonQuery.limit"
                                @clickPage="afterClickPage"
                            />
                        </div>
                    </div>
                </div>

                <div class="flex-none flex justify-center items-center">
                    <button
                        type="button"
                        @click="selectEmployee"
                        class="group w-[2.5rem] h-[2.5rem] border border-[#4261EE] rounded-xl flex justify-center items-center hover:bg-[#4261EE] hover:border-[#4261EE] transition-colors duration-150"
                    >
                        <div class="flex items-center">
                            <ChevronRightIcon
                                :width="16"
                                :height="16"
                                color="text-[#4261EE] group-hover:text-white"
                                weight="REGULAR"
                            />
                            <ChevronRightIcon
                                class="-ml-2"
                                :width="16"
                                :height="16"
                                color="text-[#4261EE] group-hover:text-white"
                                weight="REGULAR"
                            />
                        </div>
                    </button>
                </div>

                <div class="flex-1">
                    <div class="flex flex-col gap-3 h-full">
                        <div class="flex justify-between items-center">
                            <p class="font-inter text-[16px] leading-[16px] font-normal text-[#666]">
                                Selected ({{ selectedEmployeeList.length }})
                            </p>
                            <p class="font-inter text-[14px] leading-[20px] font-semibold text-red-400 cursor-pointer" @click="deleteEmployee('ALL')">
                                Delete All
                            </p>
                        </div>
                        <div class="border border-[#DDD] rounded-02 h-full overflow-y-auto ds-scrollbar" 
                            :class="{ 'border-red-400': showEmptySelectedError && selectedEmployeeList.length === 0 }"
                        >
                            <div class="min-w-full">
                                <template v-for="(employee, index) in selectedEmployeeList" v-bind:key="index">
                                    <div
                                        class="h-16 flex justify-between items-center py-2.5 w-full"
                                        :class="{ 'border-b': index !== selectedEmployeeList.length - 1 }"
                                    >
                                        <div class="w-[20rem] flex items-center gap-2 px-4">
                                            <StatusCircle :status-code="employee.employeeStatus" />
                                            <div class="flex-1 flex-col">
                                                <p class="font-inter-body-2 leading-[20px] text-[#171717] line-clamp-1 break-all">
                                                    {{ employee.employeeName }}
                                                </p>
                                                <p class="font-inter-caption-3 leading-[17px] text-[#666] line-clamp-1 break-all">
                                                    {{ employee.departmentName }}
                                                </p>
                                            </div>
                                        </div>
                                        <button class="w-[3.125rem] flex justify-center items-center" @click="deleteEmployee(employee)">
                                            <CloseIcon :width="16" :height="16" color="text-red-400" weight="FILL" />
                                        </button>
                                    </div>
                                </template>
                            </div>
                        </div>
                        <p v-if="showEmptySelectedError && selectedEmployeeList.length === 0" class="m-[1px] font-inter-body-sm-regular text-red-400">
                            Please select at least one employee to proceed.
                        </p>
                    </div>
                </div>
            </div>

            <div class="max-h-[70vh]">
                <div class="px-4 py-6 flex justify-end gap-2">
                    <DesignButton
                        class="w-20"
                        btnText="Cancel"
                        btnSize="LG"
                        btnType="QUATERNARY"
                        btnVariant="BORDER"
                        :btnDisabled="false"
                        @click-event="onClickCancelButton"
                    />
                    <DesignButton
                        class="w-20"
                        btnText="Add"
                        btnSize="LG"
                        btnType="PRIMARY"
                        btnVariant="FILL"
                        :btnDisabled="false"
                        @click-event="saveEmployee"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import DesignDropdownSelect from '@/components/design-system/design-dropdown-select.vue';
import DesignSearchInput from '@/components/design-system/design-search-input.vue';
import StatusCircle from '@/components/employee/status-circle/status-circle';
import CloseIcon from '@/components/design-system/icon/close.vue';
import ChevronRightIcon from '@/components/design-system/icon/chevron-right.vue';
import DesignPagination from '@/components/design-system/design-pagination.vue';
import DesignCheckbox from '@/components/design-system/design-checkbox.vue';
import DesignButton from '@/components/design-system/design-button.vue';
import { contractType } from '@/assets/data/optionData.js';

export default {
    components: {
        DesignDropdownSelect,
        DesignSearchInput,
        StatusCircle,
        CloseIcon,
        DesignPagination,
        DesignCheckbox,
        ChevronRightIcon,
        DesignButton,
    },
    props: {
        employeeList: Array,
        selectedList: Array,
        modelValue: Boolean,
        // employee empty 인 경우 팝업 띄우는 옵션션 -> true인 경우 직원 선택 하지 않아도 됨 / false인 경우 적어도 한명 이상의 직원 선택 필요  
        emptyOption: Boolean,
        title: String,
        showWorkScheduleOption: Boolean,
        showDepartmentOption: Boolean,
        showJobTitleOption: Boolean,
        showEmploymentTypeOption: Boolean,
        useParentDepartmentOption : Boolean, 
        useParentJobTitleOption : Boolean,
        useParentEmploymentTypeOption : Boolean,
        useParentWorkScheduleOption : Boolean,
        propDepartmentOption : { type : Array, default : function(){ return []} }, 
        propJobTitleOption : { type : Array, default : function(){ return []} },
        propEmploymentTypeOption : { type : Array, default : function(){ return []} },
        propWorkScheduleOption : { type : Array, default : function(){ return []} },
        useCancelFn : { type : Boolean, default : false},
        menuAccessLevelCode: { type: String, default: null },
    },
    watch: {
        modelValue(newVal) {
            if (!newVal) {
                return;
            }

            const self = this;
            self.employeeOptions = [];
            self.workScheduleOptions = [];
            self.departmentOptions = [];
            self.jobTitleOptions = [];
            self.employmentTypeOptions = [];
            self.searchInputString = null;
            self.searchJsonQuery = {
                limit: 10,
                offset: null,
                employeeSeqList: null,
                workScheduleSeq: null,
                departmentSeq: null,
                jobTitleSeq: null,
                employmentType : null,
            };
            self.unSelectedEmployeeList = [...self.employeeList];
            self.unSelectedEmployeeList.forEach((employee) => (employee.isChecked = false));

            let selected = [];
            if (self.selectedList) {
                selected = self.unSelectedEmployeeList.filter((employee) => self.selectedList.includes(employee.employeeSeq));
                self.unSelectedEmployeeList = self.unSelectedEmployeeList.filter((employee) => !self.selectedList.includes(employee.employeeSeq));
            }

            self.employeeCount = self.unSelectedEmployeeList.length;

            self.filteredEmployeeList = self.unSelectedEmployeeList.slice(0, self.searchJsonQuery.limit);

            self.selectedEmployeeList = selected;
            self.allCheck.isChecked = false;

            self.getEmployeeOptions();
            self.getWorkScheduleOptions();
            self.getDepartmentOptions();
            self.getJobTitleOptions();
            self.getEmploymentTypeOptions();
            self.currentPage = 1;
        },
    },
    emits: ['save-employee', 'update:modelValue', 'onClickCancel'],
    mounted() {},
    data() {
        return {
            employeeOptions: [],
            workScheduleOptions: [],
            departmentOptions: [],
            jobTitleOptions: [],
            employmentTypeOptions: [],
            searchInputString: null,
            searchJsonQuery: {
                limit: 10,
                offset: null,
                employeeSeqList: null,
                workScheduleSeq: null,
                departmentSeq: null,
                jobTitleSeq: null,
                employmentType: null,
            },
            allCheck: {
                isChecked: false,
                text: '',
                value: 'ALL',
            },
            currentPage: null,
            employeeCount: null,

            unSelectedEmployeeList: [],
            filteredEmployeeList: [],
            selectedEmployeeList: [],
            showEmptySelectedError: false,
        };
    },
    methods: {
        getEmployeeOptions() {
            const self = this;

            self.employeeOptions = self.unSelectedEmployeeList.map((emp) => ({ text: emp.employeeName, value: emp.employeeSeq }));
        },
        getDepartmentOptions() {
            const self = this;
            
            if( ! self.showDepartmentOption ) return;
            if( self.useParentDepartmentOption ){
                self.departmentOptions = [ ...self.propDepartmentOption ];
                return;
            }

            const url = self.$api('uri', 'get-department');
            const departmentQueryPayload = { showRootNode: true };
            if (self.menuAccessLevelCode) {
                departmentQueryPayload.menuAccessLevelCode = self.menuAccessLevelCode;
            }
            const json_query = JSON.stringify(departmentQueryPayload);
            self.$axios.get(url, { params: { json_query } }).then((res) => {
                const options = res.data.data.list.map((dep) => ({
                    text: dep.departmentName,
                    value: dep.departmentSeq,
                }));

                self.departmentOptions.push(...options);
            });
        },
        getJobTitleOptions() {
            const self = this;

            if( ! self.showJobTitleOption ) return;
            if( self.useParentJobTitleOption ){
                self.jobTitleOptions = [ ...self.propJobTitleOption ];
                return;
            }
            
            const json_query = JSON.stringify({
                limit: -1,
                offset: 0,
            });
            const url = self.$api('uri', 'get-jobTitle');
            self.$axios.get(url, { params: { json_query } }).then((res) => {
                const options = res.data.data.list.map((title) => ({ text: title.jobTitleName, value: title.jobTitleSeq }));

                self.jobTitleOptions.push(...options);
            });
        },
        getEmploymentTypeOptions() {
            const self = this;
            if (!self.showEmploymentTypeOption) return;
            if (self.useParentEmploymentTypeOption) {
                self.employmentTypeOptions = [ ...self.propEmploymentTypeOption ];
                return;
            }

            self.employmentTypeOptions = contractType;
        },
        getWorkScheduleOptions() {
            const self = this;
            if( ! self.showWorkScheduleOption ) return;
            if( self.useParentWorkScheduleOption ){
                self.workScheduleOptions = [ ...self.propWorkScheduleOption ];
                return;
            }
            
            const url = self.$api('uri', 'get-workSchedule');
            self.$axios.get(url).then((res) => {
                self.workScheduleOptions = res.data.data.list.map((item) => ({
                    text: item.workScheduleName,
                    value: item.workScheduleSeq,
                }));
            });
        },
        searchEmployeeData() {
            const self = this;

            self.getEmployeesListData();
            self.currentPage = 1;
        },
        getEmployeesListData(offset = 0) {
            const self = this;
            self.allCheck.isChecked = false;
            self.searchJsonQuery.offset = offset;

            // 검색어 (직원 이름 기준)
            const keyword = (self.searchInputString || '').trim().toLowerCase();

            // 아직 선택되지 않은 직원 목록
            self.unSelectedEmployeeList = self.employeeList.filter(
                (employee) => !self.selectedEmployeeSeqList().includes(employee.employeeSeq)
            );

            // 필터/검색 적용
            self.filteredEmployeeList = self.unSelectedEmployeeList.filter((employee) => {
                const matchWorkSchedule =
                    self.searchJsonQuery.workScheduleSeq == null ||
                    employee.workScheduleSeq === self.searchJsonQuery.workScheduleSeq;
                const matchDepartment =
                    self.searchJsonQuery.departmentSeq == null ||
                    employee.departmentSeq === self.searchJsonQuery.departmentSeq;
                const matchJobTitle =
                    self.searchJsonQuery.jobTitleSeq == null ||
                    employee.jobTitleSeq === self.searchJsonQuery.jobTitleSeq;
                const matchEmploymentType =
                    self.searchJsonQuery.employmentType == null ||
                    employee.employmentType === self.searchJsonQuery.employmentType;
                const matchSearch =
                    !keyword ||
                    String(employee.employeeName || '')
                        .toLowerCase()
                        .includes(keyword);

                return (
                    matchWorkSchedule &&
                    matchDepartment &&
                    matchJobTitle &&
                    matchEmploymentType &&
                    matchSearch
                );
            });

            self.employeeCount = self.filteredEmployeeList.length;
            self.filteredEmployeeList = self.filteredEmployeeList.slice(
                offset,
                offset + self.searchJsonQuery.limit
            );
        },
        afterClickPage(offset) {
            const self = this;
            self.getEmployeesListData(offset);
        },
        clickAllCheckbox(checked) {
            const self = this;
            self.allCheck.isChecked = checked;
            self.filteredEmployeeList.forEach((item) => {
                item.isChecked = checked;
            });
        },
        selectedEmployeeSeqList() {
            const self = this;
            let seqList = [];

            if (self.selectedEmployeeList) {
                seqList = self.selectedEmployeeList.map((employee) => employee.employeeSeq);
            }

            return seqList;
        },
        selectEmployee() {
            const self = this;
            const selectedEmployeeSeqSet = new Set(self.selectedEmployeeList.map((employee) => employee.employeeSeq));

            self.filteredEmployeeList.forEach((employee) => {
                if (!selectedEmployeeSeqSet.has(employee.employeeSeq) && employee.isChecked) {
                    self.selectedEmployeeList.push(employee);
                    employee.isChecked = false;
                }
            });

            self.searchEmployeeData();
        },
        deleteEmployee(deleteEmployee) {
            const self = this;

            if (deleteEmployee === 'ALL') {
                self.selectedEmployeeList = [];
                self.searchEmployeeData();
                return;
            }
            self.selectedEmployeeList = self.selectedEmployeeList.filter((item) => item.employeeSeq !== deleteEmployee.employeeSeq);
            self.searchEmployeeData();
        },
        saveEmployee() {
            const self = this;
            if (!self.emptyOption && self.selectedEmployeeList.length < 1) {
                self.showEmptySelectedError = true;
                return;
            }
            self.$emit('save-employee', self.selectedEmployeeList);
        },
        onClickCancelButton(){
            const self = this;
            self.showEmptySelectedError = false;
            self.$emit('update:modelValue', false);
            if( self.useCancelFn ){
                self.$emit('onClickCancel');
            }
        },
    },
};
</script>
